import { IconProps } from "~components";
export type SocialCardVariant = "promoter" | "competition";

interface BasePromoterOption {
  promoterAction: string;
  socialLinkType?: string;
}

export interface PromoterAction extends BasePromoterOption {
  entries?: number;
}

interface PromoterItem {
  platform: string;
  actionType: string;
  socialLinkType?: string;
  value?: number;
}

export interface SocialItemProps {
  label: string;
  icon: IconProps["icon"];
  onClick: (event: React.MouseEvent) => void;
  promoterAction: string;
  entries?: number;
}

export const isReferralPromoterAction = (promoterAction: string): boolean =>
  promoterAction === "referral:code-used";

export const toPromoterAction = (platform: string, actionType: string) =>
  `${platform}:${actionType}`;

export const fromPromoterAction = (action: string) => {
  const [platform, actionType] = action.split(":");
  return { platform, actionType };
};

export const createCompetitionTitle = (
  variant: "share" | "register",
  majorPrizeName?: string
) => {
  const vowels = ["a", "e", "i", "o", "u"];
  const messageBeginning =
    variant === "share"
      ? "Share with your friends to earn more chances to win"
      : "Register and be in to win";
  if (!majorPrizeName) return `${messageBeginning}!`;

  const omitA =
    majorPrizeName.toLowerCase().startsWith("a ") ||
    majorPrizeName.toLowerCase().startsWith("an ");

  const joinValue =
    !omitA && vowels.includes(majorPrizeName.toLowerCase().charAt(0))
      ? "an"
      : "a";

  const messageMiddle = omitA ? " " : `${joinValue} `;

  return `${messageBeginning} ${messageMiddle}${majorPrizeName ?? ""}!`;
};

export const actionsForActionType = (
  items: PromoterItem[],
  inverse = false,
  ...actionType: string[]
): PromoterAction[] => {
  return items
    ?.filter(({ actionType: type }) =>
      inverse ? !actionType.includes(type) : actionType.includes(type)
    )
    ?.map((item) => {
      const result: PromoterAction = {
        socialLinkType: item.socialLinkType,
        promoterAction: toPromoterAction(item.platform, item.actionType),
      };

      if (item.value) {
        result.entries = item.value;
      }

      return result;
    });
};

const explicitPlatformActions = [
  "facebook:share",
  "facebook:rsvp",
  "facebook:like",
  "facebook:invite",
  "twitter:share",
  "twitter:invite",
  "email:invite",
  "instagram:follow",
  "tiktok:follow",
  "messenger:invite",
] as const;

export type ExplicitPlatformActions = typeof explicitPlatformActions[number];

const socialUserInteractions = [
  "share-on-facebook",
  "share-on-twitter",
  "copy-link",
  "invite-on-messenger",
  "invite-on-twitter",
  "like-on-facebook",
  "rsvp-facebook",
  "follow-on-instagram",
  "follow-on-tiktok",
  "invite-via-email",
] as const;

export type SocialUserInteractions = typeof socialUserInteractions[number];
